import React, { useState } from 'react'
import { motion } from 'framer-motion'
import { useNavigate, Link } from 'react-router-dom'
import { useAuth } from '@/hooks/useAuth'
import LegalFooter from '@/components/common/LegalFooter'
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '@/components/common/SafeIcon';

export default function Landing() {
  const navigate = useNavigate()
  const { isAuthenticated, isLoading, user } = useAuth()
  const [phase, setPhase] = useState('idle')

  const handleEnter = () => {
    if (phase !== 'idle') return
    if (isAuthenticated) {
      setPhase('opening')
      setTimeout(() => setPhase('entering'), 800)
      setTimeout(() => navigate('/bar'), 2000)
    } else {
      navigate('/login')
    }
  }

  const frameVariants = {
    idle: { scale: 1, y: 0 },
    opening: { scale: 1, y: 0 },
    entering: { scale: 1.8, y: '8vh', transition: { duration: 1.2 } },
  }

  const doorVariants = {
    left: {
      idle: { rotateY: 0 },
      opening: { rotateY: -100, transition: { duration: 1 } },
      entering: { rotateY: -100 },
    },
    right: {
      idle: { rotateY: 0 },
      opening: { rotateY: 100, transition: { duration: 1 } },
      entering: { rotateY: 100 },
    }
  }

  return (
    <div className="relative h-[100vh] h-[100dvh] w-full flex flex-col items-center justify-center bg-[#0d0e12] overflow-hidden">
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_50%_40%,_#1f1813_0%,_#0d0e12_70%)] pointer-events-none z-0" />

      {/* 右上角登入/註冊/帳戶 */}
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: phase === 'idle' ? 1 : 0, y: phase === 'idle' ? 0 : -20 }}
        className="absolute top-6 right-6 md:top-10 md:right-10 z-50 flex items-center gap-3 safe-pt"
      >
        {!isLoading && !isAuthenticated && (
          <>
            <Link to="/login" className="text-gray-400 hover:text-amber-400 text-sm tracking-widest transition-colors">
              登入
            </Link>
            <Link to="/register" className="px-4 py-2 bg-amber-500/10 border border-amber-500/30 text-amber-400 rounded-full text-xs tracking-widest hover:bg-amber-500/20 transition-all">
              註冊
            </Link>
          </>
        )}
        {!isLoading && isAuthenticated && user && (
          <>
            <Link to="/profile" className="flex items-center gap-2 px-3 py-2 bg-white/5 border border-white/10 hover:border-amber-500/50 rounded-full text-gray-400 hover:text-amber-400 text-sm tracking-widest transition-all">
              <SafeIcon icon={FiIcons.FiUser} className="text-base" />
              <span className="hidden sm:inline">{user.name || '旅人'}</span>
            </Link>
            <button
              onClick={() => navigate('/bar')}
              className="px-4 py-2 bg-amber-500 text-black rounded-full text-xs tracking-widest hover:bg-amber-400 transition-all font-medium"
            >
              進入酒吧
            </button>
          </>
        )}
      </motion.div>

      {/* 標題 */}
      <motion.div
        variants={{
          idle: { opacity: 1, y: 0 },
          opening: { opacity: 0, y: -20 },
          entering: { opacity: 0, y: -20 },
        }}
        initial="idle"
        animate={phase}
        className="absolute top-[12%] md:top-[15%] flex flex-col items-center pointer-events-none z-20 w-full px-6 safe-pt"
      >
        <h1 className="text-xl sm:text-2xl md:text-3xl lg:text-4xl font-serif tracking-[0.2em] leading-[1.8] text-white/90 font-light text-center">
          夜深了，這座城市已經睡去。<br />
          <span className="text-amber-500">但這裡，為你留了一盞燈。</span>
        </h1>
        <p className="mt-6 md:mt-8 text-[10px] sm:text-xs md:text-sm text-gray-400 tracking-[0.4em] font-light">
          推開門，把心事放下。
        </p>
      </motion.div>

      {/* 門框 */}
      <motion.div
        variants={frameVariants}
        initial="idle"
        animate={phase}
        className="relative w-[220px] h-[360px] sm:w-[260px] sm:h-[420px] md:w-[320px] md:h-[480px] [perspective:1200px] z-10 mt-20 md:mt-32"
      >
        <div className="w-full h-full border-t-[10px] md:border-t-[14px] border-x-[10px] md:border-x-[14px] border-[#111] rounded-t-md bg-[#160f08] relative flex shadow-[0_20px_60px_rgba(0,0,0,0.6)] overflow-hidden">
          <motion.div
            variants={{ idle: { opacity: 0 }, opening: { opacity: 1 }, entering: { opacity: 1 } }}
            className="absolute inset-0 flex flex-col justify-end pointer-events-none"
          >
            <div className="absolute inset-0 bg-gradient-to-t from-amber-500/30 via-amber-700/10 to-transparent" />
            <div className="w-full h-1/2 bg-amber-400/20 blur-[40px]" />
          </motion.div>

          <motion.div
            variants={doorVariants.left}
            className="w-1/2 h-full bg-[#18120d] border-r border-black origin-left relative shadow-[inset_-5px_0_20px_rgba(0,0,0,0.5)] [transform-style:preserve-3d]"
          >
            <div className="absolute inset-2 md:inset-3 border border-white/10 rounded-sm" />
            <div className="absolute right-2 md:right-3 top-1/2 -translate-y-1/2 w-1.5 md:w-2 h-12 md:h-16 bg-gradient-to-b from-amber-300 to-amber-700 rounded-sm" />
          </motion.div>

          <motion.div
            variants={doorVariants.right}
            className="w-1/2 h-full bg-[#18120d] border-l border-black origin-right relative shadow-[inset_5px_0_20px_rgba(0,0,0,0.5)] [transform-style:preserve-3d]"
          >
            <div className="absolute inset-2 md:inset-3 border border-white/10 rounded-sm" />
            <div className="absolute left-2 md:left-3 top-1/2 -translate-y-1/2 w-1.5 md:w-2 h-12 md:h-16 bg-gradient-to-b from-amber-300 to-amber-700 rounded-sm" />
          </motion.div>
        </div>

        <motion.div
          animate={{ opacity: phase === 'idle' ? 1 : 0 }}
          className="absolute inset-y-0 left-1/2 -translate-x-1/2 w-[2px] bg-amber-500/50 blur-[2px] shadow-[0_0_20px_rgba(245,158,11,0.4)] pointer-events-none z-20"
        />

        {phase === 'idle' && (
          <div className="absolute inset-0 z-30 cursor-pointer group" onClick={handleEnter}>
            <div className="absolute inset-0 bg-white/[0.03] opacity-0 group-hover:opacity-100 transition-opacity" />
            <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 opacity-0 group-hover:opacity-100 transition-opacity flex flex-col items-center">
              {isAuthenticated && user ? (
                <span className="px-5 py-2.5 md:px-6 md:py-3 bg-black/60 border border-amber-500/40 text-amber-400 text-[10px] md:text-xs tracking-[0.4em] rounded-full backdrop-blur-sm whitespace-nowrap shadow-lg">
                  歡迎回來，{user.name || '旅人'}。推門入內
                </span>
              ) : (
                <div className="flex flex-col items-center space-y-2">
                  <span className="px-5 py-2.5 md:px-6 md:py-3 bg-black/60 border border-amber-500/40 text-amber-400 text-[10px] md:text-xs tracking-[0.4em] rounded-full backdrop-blur-sm whitespace-nowrap shadow-lg">
                    推門入內 (需登入)
                  </span>
                  <span className="text-[8px] md:text-[9px] text-gray-500 tracking-widest px-3 py-1 bg-black/40 rounded-full border border-white/5">
                    ⚠️ 限 18 歲以上成年人
                  </span>
                </div>
              )}
            </div>
          </div>
        )}
      </motion.div>

      {/* Footer */}
      <motion.div
        variants={{
          idle: { opacity: 1, y: 0 },
          opening: { opacity: 0, y: 10 },
          entering: { opacity: 0, y: 10 },
        }}
        initial="idle"
        animate={phase}
        className="absolute bottom-6 md:bottom-10 w-full z-40 safe-pb"
      >
        <LegalFooter />
      </motion.div>

      <motion.div
        variants={{
          idle: { opacity: 0 },
          opening: { opacity: 0 },
          entering: { opacity: 1 },
        }}
        initial="idle"
        animate={phase}
        className="absolute inset-0 bg-[#0d0e12] z-50 pointer-events-none"
      />
    </div>
  )
}